<template>
  <BaseModule
    v-bind="$props"
    class="icons-module"
    data-module="icons"
    :style="{
      '--title-weight': sectionTitleWeight,
    }"
  >
    <div class="content">
      <header
        v-if="sectionTitle || sectionSubtitle"
        class="section-title base-title-fontsize"
      >
        <component
          :is="titleTag"
          v-if="sectionTitle"
          class="tit tit-g"
        >
          {{ sectionTitle }}
        </component>
        <h3
          v-if="sectionSubtitle"
          class="sub"
        >
          {{ sectionSubtitle }}
        </h3>
      </header>

      <Warning
        v-if="items.length === 0 && editing"
        text="O módulo não possui nenhum bloco adicionado"
      >
      </Warning>

      <div
        v-else
        class="lista-icones"
      >
        <div
          v-for="(item, idx) in items"
          :key="item.id || idx"
          class="icone"
        >
          <figure
            v-if="item.image"
            class="img-icone"
          >
            <LImage
              :src="item.image"
              :alt="item.imageAltText"
              :no-stretch="true"
              loader-size="40"
            />
            <EditorLink
              v-if="showButton(undefined, item.buttonUrl)"
              :model="item.buttonUrl"
              class="absolute-full"
            />
          </figure>
          <div class="texts base-content-fontsize">
            <h3 class="nome-icone">
              {{ item.title }}
            </h3>
            <div
              class="descricao-icone txt"
              v-html="item.text"
            ></div>
            <EditorLink
              v-if="showButton(item.buttonText, item.buttonUrl)"
              class="cta"
              :model="item.buttonUrl"
            >
              {{ item.buttonText }}
            </EditorLink>
          </div>
        </div>
      </div>
    </div>
  </BaseModule>
</template>

<script>
import showButton from '@/mixins/showButton';
import base from '@/mixins/base';
import weightOptions from '@/helpers/weightOptions.json';
import Warning from '@/components/Warning.vue';
import EditorLink from './EditorLink.vue';
import BaseModule from './generic/BaseModule.vue';

export default {
  name: 'Icons',
  components: {
    BaseModule,
    EditorLink,
    Warning,
  },
  mixins: [showButton, base],
  inheritAttrs: false,
  props: {
    ...BaseModule.props,
    sectionTitle: {
      type: String,
      default: '',
    },
    sectionTitleWeight: {
      type: String,
      default: '300',
    },
    sectionSubtitle: {
      type: String,
      default: '',
    },
    items: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  computed: {
    editing() {
      return typeof this.$parent === 'undefined';
    },
  },
  metadata: {
    extends: BaseModule.metadata,
    tabs: [
      {
        name: 'content',
        groups: [
          {
            name: 'text',
            fields: [
              {
                name: 'sectionTitle',
                type: 'ShortText',
                label: 'Título',
                maxlength: 40,
              },
              {
                name: 'sectionTitleWeight',
                type: 'VueSelect',
                label: 'Peso do título',
                options: weightOptions,
                isEnabled({ sectionTitle }) {
                  return sectionTitle;
                },
              },
              {
                name: 'sectionSubtitle',
                type: 'ShortText',
                label: 'Subtítulo',
              },
            ],
            toRemove: ['title', 'titleWeight', 'subtitle'], // migrate these fields
          },
          {
            name: 'blocks',
            label: 'Blocos',
            fields: [
              {
                name: 'items',
                type: 'List',
                minItems: 2,
                itemLabel: 'Bloco {index}', // any {index} will be replaced by item's index (1-based)
                addItemLabel: 'Adicionar bloco',
                fields: [
                  {
                    name: 'title',
                    type: 'ShortText',
                    label: 'Título',
                    maxlength: 30,
                  },
                  {
                    name: 'text',
                    type: 'Html',
                    label: 'Texto de apoio',
                  },
                  {
                    name: 'image',
                    type: 'ImageFile',
                    label: 'Imagem',
                    helpText:
                      'Dimensões recomendadas: 70x70px para ícones e 500x500px para fotos',
                  },
                  {
                    name: 'imageAltText',
                    type: 'ShortText',
                    label: 'Texto alternativo da imagem',
                  },
                  {
                    name: 'buttonText',
                    type: 'ShortText',
                    label: 'Texto do botão',
                    maxlength: 20,
                  },
                  {
                    name: 'buttonUrl',
                    type: 'Url',
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
};
</script>

<style scoped lang="scss">
.icons-module {
  padding: 0 20px;
  padding-top: var(--padding-top);
  padding-bottom: var(--padding-bottom);

  .lista-icones {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    margin: 0 -20px;
  }

  .icone {
    text-align: center;
    align-items: center;
    justify-content: center;
    margin: 0 20px;

    .img-icone {
      margin: auto;
      margin-bottom: 21px;
      position: relative;

      display: flex;
      align-items: center;
      justify-content: center;
    }

    .nome-icone {
      font-size: 1.31em;
      text-transform: uppercase;
      font-weight: bold;
      line-height: 25px;
      margin-bottom: 10px;
      color: var(--text-color);
    }

    .descricao-icone {
      font-size: 1em;
      line-height: 19px;
      max-width: 245px;
      margin: auto;
      margin-bottom: 15px;

      &::v-deep {
        p {
          font-size: $size-normal;
        }

        a,
        a:visited {
          color: inherit;
          text-decoration: underline;
        }
      }
    }
  }
}

// Desktop only styles
@include media-breakpoint-up(lg) {
  .icone {
    width: 100%;
    max-width: 200px;
  }

  /* Two items */
  .icone:first-child:nth-last-child(2),
  .icone:first-child:nth-last-child(2) ~ .icone {
    .img-icone {
      max-width: 450px;
      max-height: 450px;
    }
  }

  /* Three items */
  .icone:first-child:nth-last-child(3),
  .icone:first-child:nth-last-child(3) ~ .icone {
    .img-icone {
      max-width: 300px;
      max-height: 300px;
    }
  }

  /* Four items */
  .icone:first-child:nth-last-child(4),
  .icone:first-child:nth-last-child(4) ~ .icone {
    .img-icone {
      max-width: 200px;
      max-height: 200px;
      min-width: 100px;
      min-height: 70px;
    }
  }
}

// Mobile medium styles
@include media-breakpoint-down(lg) {
  .icons-module {
    .section-title {
      padding-bottom: 30px;
    }

    .lista-icones {
      flex-wrap: wrap;
    }

    .icone {
      width: 45%;
      max-width: 45%;
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      flex-direction: column;
      margin: 0 15px;

      .img-icone {
        margin: 20px auto;
      }

      &:nth-child(n + 3) {
        margin-top: 40px;
      }

      .descricao-icone {
        max-width: 80%;
        margin: auto;
        margin-bottom: 15px;
      }
    }
  }
}

// Mobile small styles
@include media-breakpoint-down(sm) {
  .icons-module {
    padding-top: var(--padding-top-mobile);
    padding-bottom: var(--padding-bottom-mobile);

    .lista-icones {
      flex-wrap: wrap;
    }

    .icone {
      width: 100%;
      max-width: 100%;
    }

    .descricao-icone {
      width: 100%;
      max-width: 100% !important;
    }

    .img-icone {
      max-width: 80%;
    }

    .texts {
      width: calc(100% - 20vw);
    }
  }
}
</style>
